import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  createBookkeepingAccount,
  deleteBookkeepingAccount,
  fetchAllBookkeepingAccounts,
  fetchBookkeepingAccounts,
  updateBookkeepingAccount,
} from '~/api/bookkeeping/bookkeeping-accounts';
import { queryClient } from '~/client/queryClient';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import useExceptionLogger from '~/hooks/useExceptionLogger';
import {
  BookkeepingAccount,
  BookkeepingAccountFilters,
  BookkeepingAccountQueryKeys,
  CreateBookkeepingAccount,
  UpdateBookkeepingAccount,
} from '~/types/general-bookkeeping';

export const useAllBookkeepingAccountsSuspenseQuery = (filters: BookkeepingAccountFilters) => {
  const companyId = useActiveCompanyId();

  return useSuspenseQuery({
    queryKey: BookkeepingAccountQueryKeys.list(companyId, filters),
    queryFn: () => fetchAllBookkeepingAccounts(companyId, filters),
    staleTime: Infinity,
    select: (accounts) => {
      const accountsMap: Record<string, BookkeepingAccount> = {};
      accounts.forEach((account) => {
        accountsMap[account.id] = account;
      });
      return accountsMap;
    },
  });
};

export const useBookkeepingAccountsInfiniteQuery = (filters: BookkeepingAccountFilters = {}) => {
  const companyId = useActiveCompanyId();

  return useInfiniteQuery({
    queryKey: BookkeepingAccountQueryKeys.infiniteList(companyId, filters),
    queryFn: ({ pageParam }) =>
      fetchBookkeepingAccounts(companyId, filters, {
        cursor: pageParam || undefined,
        include: ['parent'],
      }),
    getNextPageParam: ({ page }) => page?.cursor ?? undefined,
    initialPageParam: '',
    staleTime: Infinity,
    select: (data) => data.pages.flatMap((page) => page.accounts),
  });
};

export const useAllBookkeepingAccountsQuery = (filters: BookkeepingAccountFilters = {}) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: BookkeepingAccountQueryKeys.list(companyId, filters),
    queryFn: () => fetchAllBookkeepingAccounts(companyId, filters),
    staleTime: Infinity,
  });
};

export const useAllBookkeepingAccountsWithFinancialStatementItemQuery = (
  filters: BookkeepingAccountFilters = {},
  options?: { enabled?: boolean },
) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    queryKey: BookkeepingAccountQueryKeys.listWithFinancialStatementItem(companyId, filters),
    queryFn: () => fetchAllBookkeepingAccounts(companyId, filters, { include: ['financialStatementItem'] }),
    staleTime: Infinity,
    enabled: options?.enabled,
    placeholderData: keepPreviousData,
  });
};

export const useCorrelatedBookkeepingAccountsByFinancialStatementItemIdQuery = () => {
  const companyId = useActiveCompanyId();
  const filters: BookkeepingAccountFilters = { hasChildren: false, hasFinancialStatementItem: true };

  return useQuery({
    queryKey: BookkeepingAccountQueryKeys.listWithFinancialStatementItem(companyId, filters),
    queryFn: () => fetchAllBookkeepingAccounts(companyId, filters, { include: ['financialStatementItem'] }),
    staleTime: Infinity,
    select: (accounts) => {
      const accountsByItemId = new Map<string, BookkeepingAccount[]>();
      for (const account of accounts) {
        const itemId = account.financialStatementItem?.id;
        if (!itemId) continue;
        const existing = accountsByItemId.get(itemId);
        if (existing) existing.push(account);
        else accountsByItemId.set(itemId, [account]);
      }
      return accountsByItemId;
    },
  });
};

export const useBookkeepingAccountsByCodes = (codes: (string | null)[]) => {
  const companyId = useActiveCompanyId();
  return useSuspenseQuery({
    queryKey: BookkeepingAccountQueryKeys.byCodes(companyId, codes),
    queryFn: () => fetchBookkeepingAccounts(companyId, { codes }),
    staleTime: Infinity,
  });
};

export const useBookkeepingAccountIsUsedQuery = (account: BookkeepingAccount) => {
  const companyId = useActiveCompanyId();
  const filters: BookkeepingAccountFilters = { code: account.code, used: true };
  return useQuery({
    queryKey: BookkeepingAccountQueryKeys.list(companyId, filters),
    queryFn: () => fetchBookkeepingAccounts(companyId, filters, { size: 1 }),
    select: (data) => data.accounts.length > 0,
  });
};

const invalidateBookkeepingAccountsCache = () => {
  queryClient.invalidateQueries({ queryKey: BookkeepingAccountQueryKeys.all });
};

export const useCreateBookkeepingAccountMutation = () => {
  const companyId = useActiveCompanyId();
  const { captureException } = useExceptionLogger();
  return useMutation({
    mutationFn: (values: Omit<CreateBookkeepingAccount, 'companyId'>) =>
      createBookkeepingAccount({ ...values, companyId }),
    onSuccess: invalidateBookkeepingAccountsCache,
    onError: (error, values) => captureException(error, { extra: { values } }),
  });
};

export const useUpdateBookkeepingAccountMutation = () => {
  const { captureException } = useExceptionLogger();
  return useMutation({
    mutationFn: (values: UpdateBookkeepingAccount) => updateBookkeepingAccount(values),
    onSuccess: invalidateBookkeepingAccountsCache,
    onError: (error, values) => captureException(error, { extra: { values } }),
  });
};

export const useAssignBookkeepingAccountsToFinancialStatementItemMutation = () => {
  const companyId = useActiveCompanyId();
  const { captureException } = useExceptionLogger();
  return useMutation({
    mutationFn: ({
      accounts,
      financialStatementItemId,
    }: {
      accounts: BookkeepingAccount[];
      financialStatementItemId: string;
    }) =>
      Promise.all(
        accounts.map((account) =>
          updateBookkeepingAccount({ id: account.id, name: account.name, financialStatementItemId }),
        ),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BookkeepingAccountQueryKeys.listWithFinancialStatementItem(companyId),
      });
    },
    onError: (error, values) => captureException(error, { extra: { values } }),
  });
};

export const useDeleteBookkeepingAccountMutation = () => {
  const { captureException } = useExceptionLogger();
  return useMutation({
    mutationFn: (id: BookkeepingAccount['id']) => deleteBookkeepingAccount(id),
    onSuccess: invalidateBookkeepingAccountsCache,
    onError: (error, id) => captureException(error, { extra: { id } }),
  });
};
